import { LocationBatchBadge } from '@modules/Locations/components/display/LocationBatchBadge';
import { getDisplayableStockBatchLabel, type LocationStockBatchEntry } from '@modules/Locations/models/Location';
import type { MovementBatchSummary } from '@modules/Reports/models/MovementBatch';
import { formatLineBatchDate } from '@shared/utils/formatLineBatchDate';

export interface MovementBatchCellItem {
  batch_id: number | null;
  batch: MovementBatchSummary | null;
}

interface MovementBatchCellProps {
  item: MovementBatchCellItem;
  /** Conservado por compatibilidad con llamadas existentes */
  i18nNs?: string;
}

function toStockBatchEntry(item: MovementBatchCellItem): LocationStockBatchEntry {
  return {
    batch_id: item.batch_id ?? item.batch?.id ?? null,
    batch: item.batch
      ? {
          id: item.batch.id,
          batch_number: item.batch.batch_number ?? null,
          expiry_date: item.batch.expiry_date ?? null,
        }
      : undefined,
  };
}

const EMPTY = <span className="text-xs text-complementary-text flex items-center h-full">-</span>;

/** Texto completo del lote (para autosize de columna AG Grid). */
export function getMovementBatchDisplayLabel(item: MovementBatchCellItem): string {
  if (!item.batch_id && !item.batch) return '';

  const entry = toStockBatchEntry(item);
  const batchNumber = getDisplayableStockBatchLabel(entry);
  if (batchNumber == null) return '';

  const expiry = item.batch?.expiry_date;
  const expiryLabel = expiry ? formatLineBatchDate(expiry) : '';
  return expiryLabel ? `${batchNumber} Cad. ${expiryLabel}` : batchNumber;
}

/** Lote en una sola fila (mismo patrón que Locations / LocationBatchBadge). */
export function MovementBatchCell({ item }: MovementBatchCellProps) {
  if (!item.batch_id && !item.batch) {
    return EMPTY;
  }

  const entry = toStockBatchEntry(item);
  if (getDisplayableStockBatchLabel(entry) == null) {
    return EMPTY;
  }

  const batchId = item.batch?.id ?? item.batch_id;

  return (
    <div
      className="flex items-center h-full w-max max-w-none py-0.5"
      data-testid={batchId ? `movement-batch-${batchId}` : undefined}
    >
      <LocationBatchBadge batch={entry} />
    </div>
  );
}
